import { useEffect, useMemo, useState, type ReactNode } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { MeshPanel, panelTitle } from './MeshPanel';
import { MeshRunStrip, useShownFailure, useShownRun } from './MeshRunStrip';
import { FailedRunPanel } from './FailedRunPanel';
import { MeshPlaceholder } from './MeshPlaceholder';
import { WrittenCallBadge } from './WrittenCallBadge';
import { MeshGenerateControls } from './MeshGenerateControls';
import { VIEWER_BOX } from './PartViewer';
import { DeleteRunButton, MeshStage } from './MeshStage';
import { useCatalogSwitch } from './catalogSwitch';
import { useCatalogItems } from './catalogItems';
import { schematicSrc } from './panels/CatalogItemRow';
import { touchRows, useHardwareInteractions, type HardwareInteraction } from './hardwareInteractionsRun';
import { useAssemblySteps } from './categoryBands';
import { pieceContents, useStepsRun, subStepLabel } from './stepsRun';
import { gestureKey } from './stepHolesRun';
import { JointSubSteps } from './SubStepTable';
import { bumpSetRuns, setName, setPaint, setsOf, useSetRuns, type HardwareSet } from './hardwareSets';
import { partBuildSize } from './partIdentity';
import { materialsOfPart, usePartMaterialNames, usePartMaterials, usePartMaterialsAlso } from './partMaterialRun';
import { usePartStepCrops } from './hardwareCropsRun';
import { partBuildMaterials, useToolDriven, type ToolSet } from './toolSet';
import { usePartModelJobs } from './partModels';
import { ImageLightbox } from './ImageLightbox';
import type { Material } from './materialsRun';
import { useMaterialSurfaces } from './materialSurface';
import { WrittenDimensionsPanel } from './WrittenDimensionsPanel';
import { namedIn, withLetter } from './partsCatalogue';
import { isHardwareOrTool, type PartItem } from './partsRun';
import { useSubmodelFilter } from './submodelFilterStore';
import {
  defaultEffort, defaultPasses, effortChoices, engineLabelOf, engineModelKey,
  writtenEngineOf, writtenModelLabel, generateWrittenMesh, generationStages, generationTook, newRunToken,
  passChoices, passGroup, stopWritten, withEffort, writtenPartsOf, writtenSize,
  partSizeOfMesh, builtBoxOf, dimensionsOf, isWrittenRun, type WrittenInteraction, type WrittenPart,
} from './openaiBlenderApi';
import {
  deleteJob, errorOnARow, falError, fetchStatus3D, meshSrc, meshStamp, pieceOf,
  type Job3D, type Status3D,
} from './threeDApi';
import {
  endRun, runInFlight, setGeneratorChoice, setRunProgress, startRun,
  useGeneratorChoice, useRunsInFlight,
} from './generatorMemory';
import { IS_DEMO } from '../../apiBase';
import { useLanguage } from '../../contexts/LanguageContext';

// PIECES OF HARDWARE THAT WORK TOGETHER, BUILT AS ONE MODEL — one row of Phase 4's hardware
// table (see hardwareSets). Under the part's own card in Phase 6-2, for every set it is in.
//
// The same two halves as every model card: what came back on top, the machinery below. Kept,
// the model is cut into one mesh per part and each becomes that part's model (see
// set_in_catalog), the way a kept product gives its components theirs.
//
// OR A TOOL AND THE HARDWARE IT DRIVES — the tools subphase's card (see toolSet): the same machinery,
// told which part is the tool, and shown the manual's crops of that tool beside the drawings.
export function HardwareSetStep({ docId, set, interaction, items, locked, onKept, corner, badgeOnly, file }: {
  docId: string;
  set: HardwareSet | ToolSet;
  // The hardware table's row it is — none for a tool's set, which no row of that table is.
  interaction?: HardwareInteraction;
  items: PartItem[];
  locked?: boolean;
  // The parts' own runs have changed: the pieces of a kept set are their models now.
  onKept: () => void;
  // Which of the set's models is on screen — see PartsPhase.
  corner?: ReactNode;
  // Only its generation badge, nothing on the page — see PartsPhase's hwView.
  badgeOnly?: boolean;
  // The manual, which a tool's page crops are cut from (see usePartStepCrops).
  file?: Blob | null;
}) {
  const { t, language } = useLanguage();
  const submodel = useSubmodelFilter(docId);
  // `loaded` is whether the catalog is evidence yet: a drawing it does not list is only missing
  // once it has been read — see the warning under the pictures.
  const { items: catalog, loaded: catalogLoaded } = useCatalogItems();
  // Subscribed, so a material and its colour set in Phase 3 reach the next build's paint.
  usePartMaterials(docId);
  usePartMaterialsAlso(docId);
  usePartMaterialNames(docId);
  useMaterialSurfaces(docId);
  const [probe, setProbe] = useState<Status3D | null>(null);
  useEffect(() => { void fetchStatus3D().then(setProbe).catch(() => setProbe(null)); }, []);

  // THE SET'S OWN SUB-STEPS: those its rows of the hardware table list where only hardware is
  // handled — a washer slid onto its screw — every piece of the gesture, down to what a prepared
  // piece is made of. Where hardware meets a component part, it is the component holes' to play.
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  const table = useHardwareInteractions(docId);
  const subKeys = useMemo(() => {
    if (!interaction) return [];
    const same = (r: HardwareInteraction) => r.hardware.length === interaction.hardware.length
      && r.hardware.every((id) => interaction.hardware.includes(id));
    const labels = new Set(touchRows(table).filter(same).flatMap((r) => r.substeps));
    const contents = pieceContents(steps, st.subSteps);
    return steps.flatMap((step) => (st.subSteps[step.category] ?? []).filter((sub) => {
      const parts = (sub.parts ?? []).flatMap((u) => contents.get(u.id) ?? [u.id]);
      return labels.has(subStepLabel(step.num, sub.letter)) && parts.length > 0
        && parts.every((id) => !!items[Number(id)] && isHardwareOrTool(items[Number(id)]));
    }).map((sub) => gestureKey(step.category, sub.letter)));
  }, [table, interaction, steps, st.subSteps, items]);

  const runKey = `set:${set.key}`;
  const flight = useRunsInFlight(docId)[runKey];
  const busy = !!flight;
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  // THE PICTURE OPEN LARGE, out of the drawings and the tool's crops it is built from.
  const [zoom, setZoom] = useState<string | null>(null);

  // THE SAME MODEL, EFFORT AND PASSES AS THE PART'S GENERATOR ABOVE — one choice for the
  // hardware of this manual.
  const choice = useGeneratorChoice(docId, 'parts');
  const engine = writtenEngineOf(probe) ?? '';
  const passes = choice.passes ?? defaultPasses(probe);
  const setPasses = (value: number) => setGeneratorChoice(docId, 'parts', { passes: value });
  const efforts = engine ? effortChoices(probe, engine) : [];
  const effort = choice.effort && efforts.includes(choice.effort)
    ? choice.effort : (engine ? defaultEffort(probe, engine) : '');
  const setEffort = (value: string) => setGeneratorChoice(docId, 'parts', { effort: value });
  const ready = !!engine;

  // THE PICTURES: each part's drawing from the manual's parts list — the first one it has.
  const drawings = set.parts.flatMap((p) => {
    const id = catalog.find((c) => c.id === p.itemId)?.schematics?.[0]?.id;
    return id != null ? [{ id, src: schematicSrc(id), name: p.name }] : [];
  });
  // A TOOL'S SET: which part is the tool, and where the manual draws it in use — its page crops,
  // every one, after the drawings. And on several materials (a blade, a handle), built with them and
  // left out of the paint, which would make the whole tool one of them (see partBuildMaterials).
  const tool = 'tool' in set ? set.parts.find((p) => p.at === set.tool) ?? null : null;
  const toolCrops = usePartStepCrops(docId, tool ? file ?? null : null, tool?.at);
  const toolOnMany = !!tool && materialsOfPart(docId, submodel, tool.at).length > 1;
  const notTool = (name: string) => !toolOnMany || name !== tool?.name;

  // A JOINT'S SET REUSES WHAT A TOOL DRIVES: those pieces were built in the tool's set, their
  // recesses cut against its tip, and start this build as their kept models — moved, never rebuilt
  // (see notice_3d._reused). Not built until each has one: built here, it would be a second model
  // no tool was fitted to. `waiting` is said under the pictures; null while the models load.
  const toolDriven = useToolDriven(docId);
  const { modelOf, loaded: modelsLoaded } = usePartModelJobs();
  const reused = tool ? [] : set.parts.filter((p) => toolDriven.has(p.at));
  // ITS NAME ON THE VIEWER, in the reader's language — the pieces it starts with last, in brackets
  // (see setName).
  const title = setName(set.parts,
    (p) => withLetter(namedIn(items[p.at], language) || p.name, items[p.at]?.letter),
    (p) => reused.includes(p));
  const waiting = !modelsLoaded ? null
    : reused.filter((p) => !pieceOf(modelOf.get(p.itemId))?.tool).map((p) => p.name);
  // AND A TOOL'S SET IS TOLD WHAT ITS PIECES ARE FASTENED TO in their joints, where another tool
  // drives it — the barrel nut takes the JCBC screw the Allen key's set made. The server sends the
  // ones built already (see notice_3d._partners); whichever of two is built second fits the first.
  const partners = !tool ? [] : setsOf(table.interactions, items, catalog, catalogLoaded).flatMap(({ set: joint }) =>
    set.parts.filter((p) => p.at !== tool.at && joint.parts.some((j) => j.at === p.at)).flatMap((p) =>
      joint.parts.filter((q) => toolDriven.has(q.at) && !set.parts.some((x) => x.at === q.at))
        .map((q) => ({ piece: p.name, name: q.name, itemId: q.itemId }))));

  // WHAT IT IS TOLD: the parts, each with its size, named as the pieces will be cut by; and the
  // table's row, which is what fits them together. NOT what they are made of — see `paint`.
  const parts: WrittenPart[] = set.parts.map((p) => ({
    name: p.name, qty: null,
    // The size the part's own build is told — see partBuildSize.
    size: items[p.at] ? writtenSize(partBuildSize(catalog, p.itemId, items[p.at])) : undefined,
    material: null,
  }));
  // WHAT EACH PART IS MADE OF, painted on by the build once the script has run — see setPaint,
  // the same rule a reading's apply repaints the kept model by.
  const painted = setPaint(docId, submodel, set);
  const paint = painted.paint.filter((p) => notTool(p.part));
  const uncoloured = painted.uncoloured.filter(notTool);
  const interactions: WrittenInteraction[] = tool || !interaction ? [{
    with: set.parts.map((p) => p.name), through: [], how: '', together: true,
    drawn: drawings.map((d) => d.name),
    ...(tool ? { driver: tool.name } : {}),
  }] : [{
    ...(reused.length ? { reused: reused.map((p) => ({ name: p.name })) } : {}),
    with: set.parts.map((p) => p.name),
    through: interaction.components.flatMap((id) => {
      const c = items[Number(id)];
      return c ? [withLetter(namedIn(c, 'en') || '', c.letter)] : [];
    }),
    how: interaction.descriptionEn || interaction.descriptionFr,
    together: true,
    // WHICH PART EACH PICTURE IS THE DRAWING OF — a set's pictures are not views of one object.
    drawn: drawings.map((d) => d.name),
    // Its covers, built contact face down.
    covers: set.parts.filter((p) => interaction.covers.includes(String(p.at))).map((p) => p.name),
  }];
  const subject = set.parts.map((p) => p.name).join(', ');

  const { runs: loadedRuns, failed: runsFailed } = useSetRuns(docId, set.key);
  const runs = loadedRuns ?? [];
  const done = runs.filter((j) => j.ready);
  const { shown, setShownId, showRun } = useShownRun(done);
  // A RUN THAT MADE NOTHING, where its mesh would be — see useShownFailure.
  const failedRun = useShownFailure(runs, busy);
  // A TILE PRESSED WHILE A RUN IS GOING shows its model; until then, and again once the run's own
  // tile is pressed, the box waits for the run (see ThreeDStep, where this began).
  const [peek, setPeek] = useState(false);
  useEffect(() => { if (busy) setPeek(false); }, [busy]);
  const viewKey = shown ? String(passGroup(shown, done).slice(-1)[0]?.id ?? shown.id) : undefined;

  // KEEPING IT CUTS IT: see set_in_catalog. The parts' own lists change with it.
  const catalogSwitch = useCatalogSwitch(() => { bumpSetRuns(docId); onKept(); }, setError);

  const stopRun = () => {
    const going = runInFlight(docId, runKey);
    if (!going?.token || !going.abort) return;
    void stopWritten(going.token).catch(() => {});
    going.abort.abort();
  };

  const run = async () => {
    if (busy || !drawings.length || !engine || waiting?.length !== 0) return;
    let materials: Material[] | null = null;
    try {
      materials = tool ? partBuildMaterials(docId, submodel, tool.at, t('notice.3d.buildNoSurface')) : null;
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    const going = { token: newRunToken(), abort: new AbortController() };
    startRun(docId, runKey, going);
    setError('');
    let landed = 0;
    try {
      const job = await generateWrittenMesh('schematic', drawings.map((d) => ({ id: d.id })), engine, {
        subject,
        set: {
          docId, parts: set.parts.map(({ name, itemId }) => ({ name, itemId })),
          ...(tool ? { tool: tool.itemId } : {}),
        },
        stepCrops: toolCrops.map((c) => c.src),
        reuse: reused.map((p) => ({ name: p.name })),
        partners,
        ...(materials ? { materials } : {}),
        stop: { token: going.token, signal: going.abort.signal },
        passes,
        effort: effort || undefined,
        parts,
        paint,
        interactions,
        onProgress: (progress) => {
          setRunProgress(docId, runKey, progress);
          if (progress.saved.length > landed) {
            landed = progress.saved.length;
            bumpSetRuns(docId);
            setShownId(progress.saved[progress.saved.length - 1]);
          }
        },
      });
      bumpSetRuns(docId);
      if (job) setShownId(job.id);
    } catch (e) {
      if (!axios.isCancel(e)) setError(falError(e, t('notice.3d.failed')));
      bumpSetRuns(docId);
    } finally {
      endRun(docId, runKey);
    }
  };

  const remove = async (job: Job3D) => {
    setDeletingId(job.id);
    try {
      await deleteJob(job.id);
      bumpSetRuns(docId);
    } finally {
      setDeletingId(null);
    }
  };

  const viewTitle = panelTitle(t('notice.hardwareSet.title'), withEffort(engineLabelOf(shown, probe), shown));
  const cut = catalogSwitch.pieces;

  const generator = !IS_DEMO && (
      <MeshGenerateControls
        heading={title}
        passes={passes}
        passChoices={passChoices(probe)}
        onPasses={setPasses}
        effort={effort}
        effortChoices={efforts}
        onEffort={setEffort}
        busy={busy}
        progress={flight?.progress}
        since={flight?.startedAt}
        canRun={!locked && ready && drawings.length > 0 && waiting?.length === 0}
        scope="hardware"
        onRun={() => void run()}
        onStop={stopRun}
        // THE DRAWINGS IT IS BUILT FROM, one per part — every one of them, the set being
        // all of its parts.
        pictures={(
          <div className="mb-1 flex flex-col items-center gap-2">
            {catalogLoaded ? (
              <div className="flex flex-wrap justify-center gap-2">
                {[...drawings.map((d) => ({ key: String(d.id), src: d.src })), ...toolCrops].map((c) => (
                  <img key={c.key} src={c.src} alt="" onClick={() => setZoom(c.src)}
                    className="h-16 w-16 cursor-zoom-in rounded-lg border border-slate-200 bg-white object-contain p-0.5 transition-opacity hover:opacity-80" />
                ))}
              </div>
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            )}
            {uncoloured.length > 0 && (
              <p className="text-center text-xs text-amber-600">
                {t('notice.hardwareSet.noColour').replace('{names}', uncoloured.join(', '))}
              </p>
            )}
            {!!waiting?.length && (
              <p className="text-center text-xs text-amber-600">
                {t('notice.hardwareSet.toolFirst').replace('{names}', waiting.join(', '))}
              </p>
            )}
            {catalogLoaded && drawings.length < set.parts.length && (
              <p className="text-center text-xs text-amber-600">
                {t('notice.hardwareSet.missingDrawing')}
              </p>
            )}
          </div>
        )}
        call={{
          subject,
          photos: drawings.map((d) => ({ url: d.src })),
          model: engine,
          parts,
          schema: true,
          interactions,
        }}
      />
  );
  // THE BADGE ALONE, while one of the subphase's tables is open in place of the model (see
  // PartsPhase): the phase's badges stay whatever the strip shows.
  const zoomed = (
    <ImageLightbox
      src={zoom}
      srcs={[...drawings.map((d) => d.src), ...toolCrops.map((c) => c.src)]}
      onNavigate={setZoom}
      onClose={() => setZoom(null)}
    />
  );
  if (badgeOnly) return <>{generator}{zoomed}</>;

  return (
    <div className="flex w-full flex-col items-center gap-5">
      <MeshPanel title={viewTitle} corner={corner}>
        {error && !errorOnARow(error, runs) && (
          <p className="mb-2 text-center text-sm text-rose-600">{error}</p>
        )}
        {!!cut?.skipped.length && (
          <p className="mb-2 text-center text-sm text-amber-600">
            {t('notice.3d.piecesSkipped').replace('{names}', cut.skipped.join(', '))}
          </p>
        )}
        {cut?.error && (
          <p className="mb-2 text-center text-sm text-rose-600">
            {t('notice.3d.piecesFailed').replace('{why}', cut.error)}
          </p>
        )}
        {busy && !peek ? (
          <MeshPlaceholder
            label={t('notice.3d.generating')}
            icon={<Loader2 className="h-8 w-8 animate-spin text-slate-300" />}
            className={`w-full ${VIEWER_BOX}`}
          />
        ) : failedRun.failure ? (
          <FailedRunPanel
            job={failedRun.failure}
            className={`w-full ${VIEWER_BOX}`}
            deleting={deletingId === failedRun.failure.id}
            onDelete={() => void remove(failedRun.failure!)}
            badge={(
              <WrittenCallBadge
                subject={title}
                title={t('notice.3d.runFailedCall')}
                run={failedRun.failure}
                runs={[failedRun.failure]}
                model={(failedRun.failure.model || '').replace('openai/', '')}
                parts={writtenPartsOf(failedRun.failure)}
                schema
              />
            )}
          />
        ) : shown ? (
          <MeshStage
            job={shown}
            catalog={catalogSwitch}
            src={meshSrc(shown.id, meshStamp(shown))}
            label={title}
            pieceWanted={(mesh) => partSizeOfMesh(mesh, writtenPartsOf(shown))}
            keepView={viewKey}
            // SEVERAL OBJECTS, like a product — the parts it will be cut into — and apart along
            // the one axis they share, in the order they come off it (see explodeShifts).
            explodable="axis"
            // ITS PIECES MADE UNDER TOOLS, in brackets in the parts card as in its name.
            pieceLabel={(name) => (reused.some((p) => p.name === name) ? `(${name})` : name)}
            // AND IN ITS NAME'S ORDER: its own pieces, then those made under Tools.
            pieceOrder={[...set.parts.filter((p) => !reused.includes(p)), ...reused].map((p) => p.name)}
            // What the build measured, in millimetres: what sizes the mesh on screen.
            builtMm={builtBoxOf(shown)}
            // THE NUMBERS IT WAS BUILT TO, to change and rebuild — see WrittenDimensionsPanel.
            footer={isWrittenRun(shown) && dimensionsOf(shown).length > 0 ? (
              <WrittenDimensionsPanel
                job={shown}
                disabled={locked}
                onRebuilt={(job) => { bumpSetRuns(docId); setShownId(job.id); }}
              />
            ) : undefined}
            expanded={expanded}
            onExpanded={setExpanded}
            passes={{ of: done, shown, onShow: setShownId, onDelete: (j) => void remove(j), deletingId }}
            heading={viewTitle}
            took={generationTook(shown, done)}
            stages={generationStages(shown, done)}
            className={`w-full ${VIEWER_BOX}`}
            corner={(
              <DeleteRunButton
                label={t('notice.3d.deleteModel')}
                busy={deletingId === shown.id}
                onClick={() => void remove(shown)}
              />
            )}
          />
        ) : loadedRuns === null && !busy ? (
          // NOT ASKED BACK YET: a spinner, or the failure — never "not built", which is only
          // true once the server has said so.
          runsFailed ? (
            <p className="text-center text-sm text-rose-600">{t('notice.hardwareSet.runsFailed')}</p>
          ) : (
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
          )
        ) : (
          <p className="text-center text-sm text-slate-400">
            {busy ? t('notice.3d.generating') : t('notice.hardwareSet.none')}
          </p>
        )}
        <MeshRunStrip
          ready={done}
          shown={shown}
          pending={busy ? {
            model: engineModelKey(engine),
            label: writtenModelLabel(probe, engine),
            made: done.find((j) => flight?.progress?.saved?.includes(j.id)) ?? null,
          } : null}
          engineLabel={(j) => engineLabelOf(j, probe)}
          onSelect={(j) => { setPeek(true); failedRun.leave(); setShownId(j.id); }}
          onOpenRun={(j) => { setPeek(true); failedRun.leave(); showRun(j); }}
          onOpenPending={() => setPeek(false)}
          failed={failedRun.failures}
          failedShown={failedRun.failure}
          onShowFailed={failedRun.show}
          catalog={catalogSwitch}
        />

        {/* ITS GENERATION IS THE PHASE'S BADGE, over the selector — see MeshGenerateControls. */}
        {generator}
      </MeshPanel>
      {/* AND ITS SUB-STEPS, as the steps phase tables them — the gestures the model is put together by. */}
      <JointSubSteps docId={docId} keys={subKeys} />
      {zoomed}
    </div>
  );
}
